/* eslint-disable linebreak-style */
// eslint-disable-next-line import/no-extraneous-dependencies
import { getSessionStorage, setSessionStorage } from 'utils/storageUtils';
import { dimensionMapping } from '../../constants';
// Initialize Mixpanel
let mixpanelInitialized = false;
let mixpanelModule = null;

function getMixpanelApiKey() {
  // Get Mixpanel API key from site_settings, fallback to default if not available
  if (
    typeof window !== 'undefined' &&
    window.App &&
    window.App.state &&
    window.App.state.common &&
    window.App.state.common.siteConfig &&
    window.App.state.common.siteConfig.mixpanelApiKey
  ) {
    return window.App.state.common.siteConfig.mixpanelApiKey;
  }
  // Fallback to default key for backward compatibility
  return '72b838d4add91173d5119c613c089f2c';
}

async function loadMixpanel() {
  if (!mixpanelModule) {
    try {
      // Core loader only (no rrweb/session replay) to avoid pulling in extra deps that could impact other modules
      mixpanelModule = await import(/* webpackChunkName: "mixpanel" */ 'mixpanel-browser/src/loaders/loader-module-core');
      return mixpanelModule.default;
    } catch (e) {
      // Silent fail
      return null;
    }
  }
  return mixpanelModule.default;
}

async function initMixpanel() {
  if (typeof window !== 'undefined' && !mixpanelInitialized) {
    try {
      const mixpanel = await loadMixpanel();
      if (mixpanel) {
        const apiKey = getMixpanelApiKey();
        mixpanel.init(apiKey, {
          track_pageview: false,
          persistence: 'localStorage',
        });
        mixpanelInitialized = true;
        return mixpanel;
      }
    } catch (e) {
      // Silent fail
    }
  }
  // Return cached instance if already initialized
  if (mixpanelInitialized && mixpanelModule) {
    return mixpanelModule.default;
  }
  return null;
}

function performanceHandlingForGrxCall(allowDelayInAnalyticsCall, grxTrack) {
  if (allowDelayInAnalyticsCall) {
    setTimeout(() => {
      window.grx.apply(null, grxTrack);
    }, 0);
  } else {
    window.grx.apply(null, grxTrack);
  }
}

function pushToDataLayer(gaObj) {
  window.dataLayer.push({
    label: '',
    category: '',
    type: '',
  });
  window.dataLayer.push(gaObj);
  window.dataLayer.push({
    label: '',
    category: '',
    type: '',
  });
}

function performanceHandlingForGACall(allowDelayInAnalyticsCall, gaObj) {
  if (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.pathname.includes('/gcc') &&
    !gaObj.section
  ) {
    gaObj.section = 'gcc';
  }
  if (allowDelayInAnalyticsCall) {
    setTimeout(() => {
      pushToDataLayer(gaObj);
    }, 0);
  } else {
    pushToDataLayer(gaObj);
  }
}

function callGrx(...args) {
  //Stop the call -- GRX call is coming in EU region for webview
  if (
    typeof window !== 'undefined' &&
    typeof window.grx === 'function' &&
    !(
      window.location.href.indexOf('frmapp=yes') > 0 &&
      window.geoinfo &&
      window.geoinfo.Continent === 'EU'
    )
  ) {
    const { allowDelayInAnalyticsCall } = window.App;
    if (args[0] === 'track' && typeof args[2] === 'object') {
      const grxTrack = [];
      for (let i = 0; i < args.length; i += 1) {
        grxTrack.push(args[i]);
      }
      window.grx.apply(null, grxTrack);
    } else if (
      typeof args === 'object' &&
      !getSessionStorage('grx_session_event') &&
      (args[1] === 'event' || typeof args[1] === 'object')
    ) {
      setSessionStorage('grx_session_event', true);
      const grxEvntObj = {
        category: typeof args[1] === 'object' ? args[1].eventCategory : args[2],
        action: typeof args[1] === 'object' ? args[1].eventAction : args[3],
        label: typeof args[1] === 'object' ? args[1].eventLabel : args[4],
      };
      const grxTrack = [
        'track',
        'event',
        grxEvntObj,
        args[5] && args[5].nonInteraction === 1,
      ];
      window.grx.apply(null, grxTrack);
    } else if (args[0] === 'userId' && args[1]) {
      window.grx('userId', args[1]);
    }

    if (typeof args === 'object' && args[1] === 'pageview') {
      const __ssoid =
        typeof document !== 'undefined' &&
        document.cookie.match(/(?:\s)?ssoid=(\w+);?/);
      if (__ssoid) {
        window.grx('userId', __ssoid[1]);
      }
      const grxTrack = [
        'track',
        'page_view',
        {
          url: args[2] || window.location.href,
          keywords: window.seoMetaKeywords,
          source: window.pageViewSource,
        },
      ];
      performanceHandlingForGrxCall(allowDelayInAnalyticsCall, grxTrack);
    }

    if (typeof args === 'object' && args[0] === 'set') {
      const gaDimensionKey = args[1];
      const grxDimensionKey = dimensionMapping[gaDimensionKey];
      const grxTrack = ['set', grxDimensionKey, args[2]];
      performanceHandlingForGrxCall(allowDelayInAnalyticsCall, grxTrack);
    }
  }
}

async function sendToMixpanel(gaObj) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const mixpanel = await initMixpanel();
    if (!mixpanel) {
      return;
    }
    const eventName = gaObj.event || gaObj.type || 'ga4_event';
    const props = {};
    Object.keys(gaObj).forEach(key => {
      const val = gaObj[key];
      const t = typeof val;
      if (
        key !== 'event' &&
        (t === 'string' || t === 'number' || t === 'boolean')
      ) {
        props[key] = val;
      }
    });
    mixpanel.track(eventName, props);
  } catch (e) {
    // Silent fail
  }
}

function callGa4(...args) {
  if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
    const { allowDelayInAnalyticsCall } = window.App;
    if (args && args.length > 0) {
      // window.dataLayer.push(args[0]);
      performanceHandlingForGACall(allowDelayInAnalyticsCall, args[0]);
    }
    // Send GA4 data to Mixpanel only when event category is 'abound'
    if (
      args[0] &&
      (args[0].category === 'abound' || args[0].type === 'abound')
    ) {
      sendToMixpanel(args[0]).catch(() => {});
    }
  }
}

export default function analyticsWrapper(analyticsType, ...args) {
  if (analyticsType && args.length > 0) {
    switch (analyticsType) {
      case 'grx': {
        callGrx(...args);
        break;
      }
      case 'gaAndGrx': {
        callGrx(...args);
        break;
      }
      case 'ga4': {
        callGa4(...args);
        break;
      }
      // eslint-disable-next-line no-empty
      default: {
      }
    }
  }
}
